// @flow
import {ButtonPrimary} from '@homeaway/react-buttons';
import {compose} from 'redux';
import classNames from 'classnames';
import Modal from '@homeaway/react-modal';
import {injectIntl, type IntlShape, FormattedMessage} from 'react-intl';
import React from 'react';
import type {Node} from 'react';
import keyboardConstants from '../../constants/keyboardConstants';
import './FormModal.less';

type Props = {
    /* place any template in children that might need a 'submit' and/or a 'reset' experience */
    children: Node,
    isBodyCTA?: boolean,
    headingLabel: string,
    intl: IntlShape,
    isOpen: boolean,
    modalId: string,
    /* controls whether the reset button is visible */
    resetVisible?: boolean,
    submitDisabled?: boolean,
    submitLabel: string | Node,
    onOpen?: Function,
    onClose: Function,
    onSubmit: Function,
    onReset?: Function
};

export const FormModal = (props: Props) => {
    const {
        isBodyCTA,
        headingLabel,
        intl: {formatMessage},
        isOpen,
        modalId,
        resetVisible,
        submitDisabled,
        submitLabel,
        onOpen,
        onClose,
        onSubmit,
        onReset,
        children
    } = props;

    const handleIfEnterPressed = (evt: SyntheticEvent<HTMLInputElement>) => {
        if (
            evt.nativeEvent instanceof KeyboardEvent &&
            evt.nativeEvent.keyCode === keyboardConstants.ENTER &&
            onReset
        ) {
            onReset();
        }
    };

    const title = (
        <span className={'FormModal__title'}>
            <span>{headingLabel}</span>
            {resetVisible && (
                <button
                    className="FormModal__clear"
                    data-wdio="FormModal__clear"
                    onClick={onReset}
                    onKeyDown={handleIfEnterPressed}
                >
                    <FormattedMessage
                        id="formModalResetLabel"
                        description="Button to reset form modal"
                        defaultMessage="Clear"
                    />
                </button>
            )}
        </span>
    );

    const closeText = formatMessage({
        id: 'formModalCloseText',
        defaultMessage: 'Cancel',
        description: 'Screen reader text for closing out form modal'
    });

    const ctaButton = (
        <div className="FormModal__CtaButton" key="modal-child-ctaButton">
            <ButtonPrimary
                data-wdio="modal-cta-button"
                block
                disabled={submitDisabled}
                label={submitLabel}
                onClick={onSubmit}
            />
        </div>
    );

    const footer = isBodyCTA ? null : ctaButton;

    return (
        <div
            className={classNames('FormModal', {
                'FormModal--is-body-cta': isBodyCTA
            })}
        >
            <Modal
                closeText={closeText}
                id={modalId}
                isOpen={isOpen}
                onModalOpen={onOpen}
                onClose={onClose}
                title={title}
                footer={footer}
                {...props}
                closeOnOutsideClick
                closeOnEsc
                isWorkflow
            >
                {children}
                {isBodyCTA && ctaButton}
            </Modal>
        </div>
    );
};

export default compose(injectIntl)(FormModal);
